import React from 'react';
import './AnalyticsCard.css';
import { Box, Typography, Button } from '@mui/material';
import { useNavigate } from 'react-router-dom';

const AnalyticsCard = ({
  title,
  value,
  // Signed % change vs the previous period. null means the previous period was
  // 0 (a % change from 0 is undefined), shown as "from 0". undefined hides it.
  growth,
  // Whether a rise is good news for this metric. The arrow always shows the
  // real direction; this only decides the colour (e.g. more tickets is red).
  higherIsBetter = true,
  comparisonPeriod,
  actionButton,
  // True when this card's request failed: show "--" rather than a fake 0.
  unavailable = false,
}) => {
  const navigate = useNavigate();

  const fromZero = growth === null;
  // From 0 the value can only have gone up on these cards (counts and rates).
  const direction = fromZero || growth > 0 ? 'up' : growth < 0 ? 'down' : 'none';
  const tone =
    direction === 'none'
      ? 'neutral'
      : (direction === 'up') === higherIsBetter
        ? 'positive'
        : 'negative';
  const arrow = direction === 'up' ? '↑' : direction === 'down' ? '↓' : '';

  const handleActionClick = () => {
    if (actionButton?.onClick) {
      actionButton.onClick();
    } else if (actionButton?.route) {
      navigate(actionButton.route);
    }
  };

  return (
    <Box className="analytics-card">
      <Box className="card-header">
        <Typography className="card-title">{title}</Typography>
        {actionButton && (
          <Button
            className="card-action-button"
            onClick={handleActionClick}
            size="small"
            sx={{
              textTransform: 'none',
              fontSize: '12px',
              fontWeight: 500,
              color: '#2563eb',
              padding: '2px 8px',
              minWidth: 'auto',
              '&:hover': {
                backgroundColor: 'rgba(37, 99, 235, 0.08)',
              },
            }}
          >
            {actionButton.label || 'View'}
          </Button>
        )}
      </Box>
      <Typography className="card-value">{unavailable ? '--' : value}</Typography>
      {!unavailable &&
        growth !== undefined &&
        comparisonPeriod !== null &&
        comparisonPeriod !== '' &&
        comparisonPeriod !== 'lifetime' && (
          <Typography className={`card-percentage ${tone}`}>
            {arrow}{' '}
            {fromZero
              ? `from 0 ${comparisonPeriod}`
              : `${Math.abs(growth)}% vs ${comparisonPeriod}`}
          </Typography>
        )}
    </Box>
  );
};

// Placeholder for a non-card section (donut, table, list) whose request
// failed, so one failed request does not blank the rest of the tab.
export const SectionUnavailable = ({ title }) => (
  <Box className="analytics-card">
    {title && <Typography className="card-title">{title}</Typography>}
    <Typography className="card-value">--</Typography>
  </Box>
);

// Data of one Promise.allSettled result, or null if that request failed.
export const settledData = (result, label) => {
  if (result.status === 'fulfilled') return result.value.data;
  console.error(`Error fetching ${label}:`, result.reason);
  return null;
};

// Reads a growth_percentage from the API. Keeps null (previous period was 0,
// shown as "from 0") apart from a missing value (no data yet, shown as 0%).
export const readGrowth = value => (value === null ? null : Number(value) || 0);

export default AnalyticsCard;
